import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  Check, 
  X, 
  AlertTriangle,
  GripVertical,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

export default function ManageRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRoleName, setNewRoleName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const data = await api.get('/roles/all');
      setRoles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newRoleName.trim()) return;

    setSaving(true);
    setError(null);
    try {
      const role = await api.post('/roles', { name: newRoleName.trim() });
      setRoles([...roles, role]);
      setNewRoleName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id) => {
    if (!editingName.trim()) return;

    setSaving(true);
    setError(null);
    try {
      const role = await api.put(`/roles/${id}`, { name: editingName.trim() });
      setRoles(roles.map(r => r.id === id ? role : r));
      setEditingId(null);
      setEditingName('');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id, currentEnabled) => {
    try {
      const role = await api.put(`/roles/${id}`, { enabled: !currentEnabled });
      setRoles(roles.map(r => r.id === id ? role : r));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete "${name}"? This cannot be undone.`)) return;

    try {
      await api.delete(`/roles/${id}`);
      setRoles(roles.filter(r => r.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (role) => {
    setEditingId(role.id);
    setEditingName(role.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-hub-200 border-t-hub-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Manage Roles</h1>
        <p className="text-gray-500 mt-1">Service types for gig assignments and invoicing</p>
      </div>

      {/* QuickBooks Warning */}
      <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
        <div className="flex gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
          <div>
            <p className="font-semibold text-amber-800">QuickBooks Integration Notice</p>
            <p className="text-sm text-amber-700 mt-1">
              These role names must match <strong>exactly</strong> the names of Products and Services in your QuickBooks account. 
              Failure to do so will result in rejected invoices when pushing to QuickBooks.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Add New Role */}
      <form onSubmit={handleAdd} className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            placeholder="Enter new role name (e.g., DJ Services)"
            className="input flex-1"
          />
          <button 
            type="submit" 
            className="btn-primary"
            disabled={saving || !newRoleName.trim()}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Role
          </button>
        </div>
      </form>

      {/* Roles List */}
      <div className="card">
        <div className="card-body p-0">
          {roles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No roles defined yet.</p>
              <p className="text-sm mt-1">Add your first role above.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {roles.map((role) => (
                <li 
                  key={role.id} 
                  className={`flex items-center gap-3 p-4 ${!role.enabled ? 'bg-gray-50' : ''}`}
                >
                  <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
                  
                  {editingId === role.id ? (
                    <>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="input flex-1"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEdit(role.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                      />
                      <button 
                        onClick={() => handleEdit(role.id)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                        disabled={saving}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={cancelEdit}
                        className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={`flex-1 ${!role.enabled ? 'text-gray-400' : ''}`}>
                        {role.name}
                      </span>
                      
                      {/* Enable/Disable Toggle */}
                      <button
                        onClick={() => handleToggle(role.id, role.enabled)}
                        className={`p-1 rounded ${role.enabled ? 'text-green-600' : 'text-gray-400'}`}
                        title={role.enabled ? 'Click to disable' : 'Click to enable'}
                      >
                        {role.enabled ? (
                          <ToggleRight className="w-6 h-6" />
                        ) : (
                          <ToggleLeft className="w-6 h-6" />
                        )}
                      </button>
                      
                      {/* Edit Button */}
                      <button 
                        onClick={() => startEdit(role)}
                        className="p-2 text-gray-400 hover:text-hub-600 hover:bg-hub-50 rounded-lg"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      
                      {/* Delete Button */}
                      <button 
                        onClick={() => handleDelete(role.id, role.name)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-6 text-sm text-gray-500">
        <p><strong>Tips:</strong></p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Disabled roles won't appear in the scheduler dropdown</li>
          <li>Role names are used directly as Product/Service names when creating QuickBooks invoices</li>
          <li>Common examples: "DJ Services", "Live Music", "Band Performance", "Acoustic Set"</li>
        </ul>
      </div>
    </div>
  );
}
